import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Users, Mail, Send, ScrollText, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminMembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/members").then((r) => setMembers(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Member Roster</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
              Members
            </h1>
            <p className="text-[#4A4A4A] mt-3 max-w-2xl">
              Tap a member to open their full dossier — contact details, sizing, style preferences, recent orders and saved wishlist on one page.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/staff"
              data-testid="members-staff-link"
              className="btn-secondary text-xs inline-flex items-center gap-2"
            >
              <Users size={14} /> Staff
            </Link>
            <Link
              to="/admin/pending-memberships"
              data-testid="members-pending-link"
              className="btn-secondary text-xs inline-flex items-center gap-2"
            >
              <Users size={14} /> Pending
            </Link>
            <Link
              to="/admin/invitations"
              data-testid="members-invitations-link"
              className="btn-secondary text-xs inline-flex items-center gap-2"
            >
              <Send size={14} /> Invitations
            </Link>
            <Link
              to="/admin/audit"
              data-testid="members-audit-link"
              className="btn-secondary text-xs inline-flex items-center gap-2"
            >
              <ScrollText size={14} /> Audit
            </Link>
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              data-testid="members-invite-trigger"
              className="btn-secondary text-xs inline-flex items-center gap-2"
            >
              <Mail size={14} /> Invite member
            </button>
            <Link
              to="/admin/members/import"
              data-testid="members-import-link"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Upload size={16} /> Import Roster
            </Link>
          </div>
        </div>

        <div className="mt-8 surface-card overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Clubs</th>
                <th className="px-6 py-4">Phone</th>
                <th className="px-6 py-4">Joined</th>
                <th className="px-6 py-4 text-right">Dossier</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-6 py-8 text-center text-[#7A7A7A]">Loading…</td></tr>}
              {!loading && members.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-[#7A7A7A]">No members yet.</td></tr>
              )}
              {members.map((m) => (
                <tr
                  key={m.user_id}
                  data-testid={`member-row-${m.user_id}`}
                  className="border-t border-[#E5E3D8] hover:bg-[#FAF9F2]"
                >
                  <td className="px-6 py-4 font-serif text-lg text-[#1A1A1A]">
                    <Link
                      to={`/admin/members/${m.user_id}`}
                      data-testid={`member-open-${m.user_id}`}
                      className="hover:underline underline-offset-4"
                    >
                      {m.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A4A4A]">{m.email}</td>
                  <td className="px-6 py-4 text-sm">
                    {(m.clubs && m.clubs.length > 0) ? (
                      <ul className="space-y-1">
                        {m.clubs.map((c, i) => (
                          <li key={`${c.name}-${i}`} className="leading-tight">
                            <span className="text-[#1A1A1A]">{c.name}</span>
                            <span className="text-[#7A7A7A] mx-1">·</span>
                            <span className="font-mono text-[#1A422D]">#{c.member_number}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-[#a8a8a8]">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A4A4A]">{m.phone || m.mobile_number || "—"}</td>
                  <td className="px-6 py-4 text-sm text-[#7A7A7A]">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/admin/members/${m.user_id}`}
                      className="text-sm text-[#1A422D] underline underline-offset-4"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {inviteOpen && (
        <InviteMemberModal
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            // Refresh roster — the invitee may already have an account that
            // belonged to this club (idempotent path).
            api.get("/admin/members").then((r) => setMembers(r.data));
          }}
        />
      )}
    </div>
  );
}

function InviteMemberModal({ onClose, onInvited }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setFeedback(null);
    try {
      const { data } = await api.post("/admin/members/invite", { email: email.trim(), name: name.trim() || undefined });
      if (data.already_member) {
        setFeedback({ kind: "warn", text: `${email} is already on your roster — no invite sent.` });
      } else if (data.invitation_sent) {
        const detail = data.existing_account
          ? `Invitation emailed to ${email}. They'll be asked to sign in to accept.`
          : `Invitation emailed to ${email}. Link expires in 7 days.`;
        setFeedback({ kind: "ok", text: detail });
        onInvited && onInvited();
        // Auto-clear and prep for the next invite
        setEmail("");
        setName("");
      } else {
        setFeedback({ kind: "warn", text: `Invite recorded but email delivery failed — share this link manually: ${data.register_url}` });
      }
    } catch (err) {
      setFeedback({ kind: "error", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      data-testid="invite-member-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#F9F8F4] border border-[#E5E3D8] w-full max-w-md">
        <div className="px-6 py-4 border-b border-[#E5E3D8] flex items-center justify-between">
          <div>
            <div className="overline">Roster</div>
            <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Invite a member</h2>
          </div>
          <button onClick={onClose} type="button" data-testid="invite-member-close" className="text-[#7A7A7A] hover:text-[#1A1A1A]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            They&rsquo;ll receive an email with a one-click link to your club&rsquo;s join page, with their email pre-filled.
          </p>
          <label className="block">
            <span className="overline">Member email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="member@example.com"
              autoFocus
              data-testid="invite-member-email"
              className="mt-2 w-full px-3 py-2 border border-[#E5E3D8] bg-white font-mono text-sm focus:outline-none focus:border-[#1A422D]"
            />
          </label>
          <label className="block">
            <span className="overline">Name (optional)</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="So we can address them by name"
              data-testid="invite-member-name"
              className="mt-2 w-full px-3 py-2 border border-[#E5E3D8] bg-white text-sm focus:outline-none focus:border-[#1A422D]"
            />
          </label>
          {feedback && (
            <div
              data-testid="invite-member-feedback"
              className={`px-3 py-2 text-sm border ${
                feedback.kind === "ok"
                  ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                  : feedback.kind === "warn"
                  ? "border-[#B59A5A] bg-[#F8F1DE] text-[#8a6f33]"
                  : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
              }`}
            >
              {feedback.text}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary text-xs">Done</button>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              data-testid="invite-member-submit"
              className="btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={14} /> {busy ? "Sending…" : "Send invitation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
